import { memo, MouseEvent, useMemo } from "react";
import {
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
  EdgeProps,
  Position,
  useStore,
} from "reactflow";
import ClickableBaseEdge_ from "./clickableBaseEdge";
import {
  Divider,
  Stack,
  Typography,
  useTheme,
  Box,
  Theme,
  lighten,
  alpha,
} from "@mui/material";
import { convertBitsToBytes } from "../../../utils/easyMaps/convertToBytes";
import { useOutletContext } from "react-router-dom";
import { Close, Download, Upload } from "@mui/icons-material";
import { useStyles } from "./styles";

export const getSpecialPath = (
  { sourceX, sourceY, targetX, targetY }: any,
  offset: number,
) => {
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  return `M ${sourceX} ${sourceY} Q ${centerX} ${
    centerY + offset
  } ${targetX} ${targetY}`;
};

interface PositionHandler {
  x: number;
  y: number;
  active?: number;
  location?: {
    long: number;
    lat: number;
  };
}

interface ViewProps {
  theme: Theme;
  data: any;
  isDown: boolean;
  editMode: boolean;
  configLayer: any;
  mapId: string;
  convertInputTraffic: { valor: string | number; unidade: string };
  convertOutputTraffic: { valor: string | number; unidade: string };
  inputPower: number | string;
  outputPower: number | string;
  corTrafficRx?: { cor: string };
  corTrafficTx?: { cor: string };
  corPowerRx?: { cor: string };
  corPowerTx?: { cor: string };
  entangled?: boolean;
  interfaceName?: string;
  styles: any;
  labelScale?: number;
}

export const DetailedView: React.FC<ViewProps> = ({
  theme,
  data,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  interfaceName,
  styles,
  labelScale,
}) => (
  <Stack sx={styles.stackView(isDown, entangled, false)}>
    <Typography sx={styles.interfaceLabel(theme, labelScale)}>
      {interfaceName && interfaceName?.trim() !== ""
        ? interfaceName
        : data?.interface || "Interface"}
    </Typography>

    {isDown && !editMode ? (
      <Box sx={styles.statusContainer(theme)}>
        <Close sx={{ fontSize: `${12 * (labelScale || 1)}px` }} />
        <Typography component="span" sx={styles.statusText(labelScale)}>
          Offline
        </Typography>
      </Box>
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corTrafficTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corTrafficRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corPowerTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />
            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corPowerRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Stack>
);

export const SimpleView: React.FC<ViewProps> = ({
  theme,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  styles,
  labelScale,
}) => (
  <Stack sx={styles.stackSimpleView(isDown, entangled)}>
    {isDown && !editMode ? (
      <Box sx={styles.statusContainer(theme)}>
        <Close sx={{ fontSize: `${12 * (labelScale || 1)}px` }} />
        <Typography component="span" sx={styles.statusText()}>
          Offline
        </Typography>
      </Box>
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corTrafficTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corTrafficRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corPowerTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corPowerRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Stack>
);

export const CompactView: React.FC<ViewProps> = ({
  theme,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  styles,
  labelScale,
}) => (
  <Box sx={styles.stackView(isDown, entangled, true)}>
    {isDown && !editMode ? (
      <Close
        sx={{
          fontSize: `${14 * (labelScale || 1)}px`,
          color: theme.palette.error.main,
        }}
      />
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corTrafficTx?.cor, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade?.[0]}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corTrafficRx?.cor, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade?.[0]}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corPowerTx?.cor)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                d
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corPowerRx?.cor)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>{" "}
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                d
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Box>
);

const ClickableBaseEdge = ClickableBaseEdge_ as any;

interface EdgeAnimationProps {
  path: string;
  stroke: string;
  larguraLinha: number;
  isRx: boolean;
  mode: string;
}

const EdgeAnimation = memo(
  ({ path, stroke, larguraLinha, isRx, mode }: EdgeAnimationProps) => {
    return (
      <g>
        <animateMotion
          dur="1.5s"
          repeatCount="indefinite"
          calcMode="linear"
          {...(isRx ? { keyTimes: "0;1", keyPoints: "1;0" } : {})}
          path={path}
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 1.6 : 10}
          fill={stroke}
          opacity="0.15"
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 1.0 : 8}
          fill={stroke}
          opacity="0.4"
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 0.5 : 5}
          fill={mode === "dark" ? lighten(stroke, 0.3) : lighten(stroke, 0.6)}
          style={{
            filter: `drop-shadow(0 0 8px ${stroke})`,
          }}
        />
      </g>
    );
  },
);

EdgeAnimation.displayName = "EdgeAnimation";

const LABEL_SIZE_MIN = 1;
const LABEL_SIZE_MAX = 50;
const LABEL_SCALE_MAX = 6;

const computeLabelScale = (size: number) => {
  const clamped = Math.min(
    Math.max(Number(size) || LABEL_SIZE_MIN, LABEL_SIZE_MIN),
    LABEL_SIZE_MAX,
  );
  return (
    1 +
    ((clamped - LABEL_SIZE_MIN) * (LABEL_SCALE_MAX - 1)) /
      (LABEL_SIZE_MAX - LABEL_SIZE_MIN)
  );
};

const PositionableEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  data,
  selected,
}: EdgeProps) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isModeLight = theme.palette.mode === "light";
  const styles = useStyles(theme, isModeLight);
  const { configLayer } = useOutletContext<any>();
  const { getEdges, setEdges, screenToFlowPosition } = useReactFlow();
  const isAnyEdgeSelected = useStore((store: any) =>
    store.edges.some((edge: any) => edge.selected),
  );
  const {
    mapId,
    editMode,
    entangled,
    lineType,
    viewType,
    edgeLabelSize,
    mapRef,
    animation = "desativada",
    showLabelEnlace,
    easyMapsConfig,
    variant = "trafego",
    corTrafficTx,
    corTrafficRx,
    corPowerTx,
    corPowerRx,
    corTraffic,
    corPower,
    isDown,
    interfaceName,
    labelT,
  } = data;

  const edges = getEdges();
  const positionHandlers: PositionHandler[] = data?.positionHandlers ?? [];

  const handlersKey = positionHandlers.map((h) => `${h.x},${h.y}`).join(";");
  const colors = useMemo(() => {
    if (variant === "trafego") {
      return corTraffic?.cor || "gray";
    } else if (variant === "potencia") {
      return corPower?.cor || "gray";
    }
    return "gray";
  }, [data, id, variant]);

  const convertOutputTraffic = useMemo(
    () => convertBitsToBytes(data?.tx_traffic),
    [data],
  );
  const convertInputTraffic = useMemo(
    () => convertBitsToBytes(data?.rx_traffic),
    [data],
  );
  const inputPower = useMemo(
    () => (isNaN(data?.rx_power) ? "-" : data?.rx_power),
    [data],
  );
  const outputPower = useMemo(
    () => (isNaN(data?.tx_power) ? "-" : data?.tx_power),
    [data],
  );

  const viewTypeLabel =
    viewType && viewType.trim() !== ""
      ? viewType
      : easyMapsConfig?.configuracoes?.viewType;

  const resolvedEdgeLabelSize =
    edgeLabelSize && Number(edgeLabelSize) > 0
      ? Number(edgeLabelSize)
      : easyMapsConfig?.configuracoes?.edgeLabelSize || 0;

  const labelScale = computeLabelScale(resolvedEdgeLabelSize);

  const lineAnimation =
    easyMapsConfig?.configuracoes?.animacao?.corredorLinha ?? true;

  const { txPath, rxPath, midPoint } = useMemo(() => {
    let pathFunction: any;

    switch (lineType) {
      case "reta":
        pathFunction = getStraightPath;
        break;
      case "ortogonal":
        pathFunction = getSmoothStepPath;
        break;
      case "curvada":
        pathFunction = getBezierPath;
        break;
      default:
        pathFunction = getStraightPath;
    }

    const polylinePoints = [
      { x: sourceX, y: sourceY },
      ...positionHandlers.map((h) => ({ x: h.x, y: h.y })),
      { x: targetX, y: targetY },
    ];

    const { midPoint, midSegmentIndex, midRatio } = (() => {
      const lens = [];
      let total = 0;
      for (let i = 0; i < polylinePoints.length - 1; i++) {
        const dx = polylinePoints[i + 1].x - polylinePoints[i].x;
        const dy = polylinePoints[i + 1].y - polylinePoints[i].y;
        const len = Math.hypot(dx, dy);
        lens.push(len);
        total += len;
      }
      if (total === 0) {
        return { midPoint: polylinePoints[0], midSegmentIndex: 0, midRatio: 0 };
      }
      // Ponto da caixa: fração salva pelo organizar layout, ou o meio
      const half =
        total *
        (typeof labelT === "number" ? Math.min(0.98, Math.max(0.02, labelT)) : 0.5);
      let acc = 0;
      for (let i = 0; i < lens.length; i++) {
        if (acc + lens[i] >= half) {
          const ratio = lens[i] === 0 ? 0 : (half - acc) / lens[i];
          const p0 = polylinePoints[i];
          const p1 = polylinePoints[i + 1];
          return {
            midPoint: {
              x: p0.x + (p1.x - p0.x) * ratio,
              y: p0.y + (p1.y - p0.y) * ratio,
            },
            midSegmentIndex: i,
            midRatio: ratio,
          };
        }
        acc += lens[i];
      }
      const lastIndex = polylinePoints.length - 1;
      return {
        midPoint: polylinePoints[lastIndex],
        midSegmentIndex: Math.max(lens.length - 1, 0),
        midRatio: 1,
      };
    })();

    // Na "curvada", a tangente em cada ponto de curva segue o eixo dominante da
    // direção anterior→próximo. Sempre left/right fazia a curva virar um "<" nos
    // pontos de curva de enlaces verticais.
    const ladosCurvada = (p0: any, p1: any) => {
      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      if (Math.abs(dx) >= Math.abs(dy)) {
        return dx < 0
          ? { saida: "left", chegada: "right" }
          : { saida: "right", chegada: "left" };
      }
      return dy < 0
        ? { saida: "top", chegada: "bottom" }
        : { saida: "bottom", chegada: "top" };
    };
    const tangentes = polylinePoints.map((_, k) =>
      lineType === "curvada" && k > 0 && k < polylinePoints.length - 1
        ? ladosCurvada(polylinePoints[k - 1], polylinePoints[k + 1])
        : null,
    );

    // Parte uma bezier cúbica em t (de Casteljau): TX e RX ficam sendo a mesma
    // curva, em vez de duas curvas com tangente horizontal forçada no meio.
    const partirBezier = (edgePath: string, t: number) => {
      const n = edgePath.match(/-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/g)?.map(Number);
      if (!n || n.length !== 8) return null;
      const [x0, y0, x1, y1, x2, y2, x3, y3] = n;
      const L = (a: number, b: number) => a + (b - a) * t;
      const ax = L(x0, x1), ay = L(y0, y1);
      const bx = L(x1, x2), by = L(y1, y2);
      const cx = L(x2, x3), cy = L(y2, y3);
      const abx = L(ax, bx), aby = L(ay, by);
      const bcx = L(bx, cx), bcy = L(by, cy);
      const mx = L(abx, bcx), my = L(aby, bcy);
      return {
        primeira: `M${x0},${y0} C${ax},${ay} ${abx},${aby} ${mx},${my}`,
        segunda: `M${mx},${my} C${bcx},${bcy} ${cx},${cy} ${x3},${y3}`,
        meio: { x: mx, y: my },
      };
    };

    const buildEdge = (
      p0: any,
      p1: any,
      isFirst: boolean,
      isLast: boolean,
      lados?: { saida?: string; chegada?: string },
    ) => {
      const segDifX = p1.x - p0.x;
      const _sourcePosition = isFirst
        ? sourcePosition
        : (lados?.saida ?? (segDifX < 0 ? "left" : "right"));
      const _targetPosition = isLast
        ? targetPosition
        : (lados?.chegada ?? (segDifX < 0 ? "right" : "left"));
      // getSmoothStepPath creates loops when nodes are axis-aligned with the
      // handle direction (no room for the orthogonal step). Fall back to straight.
      const segDifY = p1.y - p0.y;
      const isVerticalHandle =
        _sourcePosition === "bottom" || _sourcePosition === "top";
      const isHorizontalHandle =
        _sourcePosition === "left" || _sourcePosition === "right";
      const ALIGN_THRESHOLD = 3;
      const isDegenerate =
        pathFunction === getSmoothStepPath &&
        ((isVerticalHandle && Math.abs(segDifX) < ALIGN_THRESHOLD) ||
          (isHorizontalHandle && Math.abs(segDifY) < ALIGN_THRESHOLD));

      const fn = isDegenerate ? getStraightPath : pathFunction;
      const [edgePath, labelX, labelY] = fn({
        sourceX: p0.x,
        sourceY: p0.y,
        sourcePosition: _sourcePosition as Position,
        targetX: p1.x,
        targetY: p1.y,
        targetPosition: _targetPosition as Position,
      });
      return { edgePath, labelX, labelY };
    };

    const txPath = [];
    const rxPath = [];
    let pontoRotulo = midPoint;
    const lastSegmentIndex = polylinePoints.length - 2;
    for (let i = 0; i <= lastSegmentIndex; i++) {
      const from = polylinePoints[i];
      const to = polylinePoints[i + 1];
      const isFirst = i === 0;
      const isLast = i === lastSegmentIndex;
      const lados = {
        saida: tangentes[i]?.saida,
        chegada: tangentes[i + 1]?.chegada,
      };
      if (i < midSegmentIndex) {
        txPath.push({
          ...buildEdge(from, to, isFirst, false, lados),
          insertIndex: i,
        });
      } else if (i > midSegmentIndex) {
        rxPath.push({
          ...buildEdge(from, to, false, isLast, lados),
          insertIndex: i,
        });
      } else if (midRatio > 0 && midRatio < 1) {
        const partes =
          lineType === "curvada"
            ? partirBezier(
                buildEdge(from, to, isFirst, isLast, lados).edgePath as string,
                midRatio,
              )
            : null;
        if (partes) {
          pontoRotulo = partes.meio;
          txPath.push({
            edgePath: partes.primeira,
            labelX: partes.meio.x,
            labelY: partes.meio.y,
            insertIndex: i,
          });
          rxPath.push({
            edgePath: partes.segunda,
            labelX: partes.meio.x,
            labelY: partes.meio.y,
            insertIndex: i,
          });
        } else {
          txPath.push({
            ...buildEdge(from, midPoint, isFirst, false, { saida: lados.saida }),
            insertIndex: i,
          });
          rxPath.push({
            ...buildEdge(midPoint, to, false, isLast, { chegada: lados.chegada }),
            insertIndex: i,
          });
        }
      } else if (midRatio === 1) {
        txPath.push({
          ...buildEdge(from, to, isFirst, isLast, lados),
          insertIndex: i,
        });
      } else {
        rxPath.push({
          ...buildEdge(from, to, isFirst, isLast, lados),
          insertIndex: i,
        });
      }
    }

    return { txPath, rxPath, midPoint: pontoRotulo };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    lineType,
    handlersKey,
    labelT,
  ]);

  const baseOpacity = entangled ? 1 : 0.33;
  const edgeOpacity = isAnyEdgeSelected && !selected ? 0.4 : baseOpacity;
  const larguraLinha = editMode
    ? 4
    : Math.max(1, easyMapsConfig?.configuracoes?.larguraEnlace ?? 6);

  const handleMouseMove = (event: any, id: string, handlerIndex: number) => {
    let activeEdge = parseInt(event.target.dataset.active ?? "-1");
    if (activeEdge === -1) {
      return;
    }
    if (activeEdge === 1) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const edgeIndex = edges.findIndex((edge) => edge.id === id);
      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;
        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers[handlerIndex] = mapRef
          ? {
              x: position.x,
              y: position.y,
              active: 1,
              location: {
                long: latlng.lng,
                lat: latlng.lat,
              },
            }
          : {
              x: position.x,
              y: position.y,
              active: 1,
            };

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
  };
  const handleMouseUp = () => {
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        return prevEdges.map((edge) => {
          if (!edge.data?.positionHandlers?.some((h: any) => h.active !== -1))
            return edge;
          return {
            ...edge,
            data: {
              ...edge.data,
              positionHandlers: edge.data.positionHandlers?.map(
                (handler: any) => ({
                  ...handler,
                  active: -1,
                }),
              ),
            },
          };
        });
      });
    }
    return;
  };

  const handleMouseDown = (
    event: any,
    id: string,
    handlerIndex: number,
    active: number,
  ) => {
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;
        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers[handlerIndex] = { ...newHandlers[handlerIndex], active: 1 };

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
      event.preventDefault();
    }
    event.preventDefault();
    return;
  };

  const handleDeleteEdge = (event: any, id: string, handlerIndex: number) => {
    event.preventDefault();
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;

        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];
        newHandlers.splice(handlerIndex, 1);

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
    return;
  };

  const handleCreateEdge = (event: any, id: string, index: number) => {
    if (selected && editMode) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);

      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;

        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers.splice(
          index,
          0,
          mapRef
            ? {
                x: position.x,
                y: position.y,
                active: -1,
                location: {
                  long: latlng.lng,
                  lat: latlng.lat,
                },
              }
            : {
                x: position.x,
                y: position.y,
                active: -1,
              },
        );

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
  };

  const positionLabel = midPoint;

  const viewProps: ViewProps = {
    theme,
    data,
    isDown,
    editMode,
    configLayer,
    mapId,
    convertInputTraffic,
    convertOutputTraffic,
    inputPower,
    outputPower,
    corTrafficRx,
    corTrafficTx,
    corPowerRx,
    corPowerTx,
    entangled,
    interfaceName,
    styles,
    labelScale,
  };

  const renderView = () => {
    switch (viewTypeLabel) {
      case "compact":
        return <CompactView {...viewProps} />;
      case "simple":
        return <SimpleView {...viewProps} />;
      case "detailed":
      default:
        return <DetailedView {...viewProps} />;
    }
  };

  const txStroke = selected
    ? (theme.palette as any).color.zabbixSeverty.selectedTranslucid
    : editMode
      ? "#5e5e5e"
      : variant === "potencia"
        ? data?.corPowerTx?.cor || colors
        : data?.corTrafficTx?.cor || colors;

  const rxStroke = selected
    ? (theme.palette as any).color.zabbixSeverty.selectedTranslucid
    : editMode
      ? "#5e5e5e"
      : variant === "potencia"
        ? data?.corPowerRx?.cor || colors
        : data?.corTrafficRx?.cor || colors;

  return (
    <>
      {rxPath.map(({ edgePath, insertIndex }, index) => (
        <ClickableBaseEdge
          onDoubleClick={(event: any) =>
            handleCreateEdge(event, id, insertIndex)
          }
          key={`edge${id}_rx_${index}`}
          path={edgePath as string}
          markerEnd={
            index === rxPath.length - 1 ? (markerEnd as string) : "false"
          }
          className={isDown && !editMode ? "edge-pulse" : ""}
          style={{
            ...styles.clickableBaseEdge(
              selected,
              editMode,
              colors,
              edgeOpacity,
              larguraLinha,
              animation,
              isDown,
              easyMapsConfig,
            ),
            stroke: rxStroke,
            animationDirection: isDown ? "normal" : "reverse",
          }}
        />
      ))}
      {lineAnimation &&
        animation !== "desativada" &&
        !isDown &&
        !editMode &&
        ((variant === "trafego" &&
          !isNaN(data?.rx_traffic) &&
          data?.rx_traffic !== 0) ||
          (variant === "potencia" &&
            !isNaN(data?.rx_power) &&
            data?.rx_power !== 0)) &&
        rxPath.length > 0 && (
          <EdgeAnimation
            key={`rx-anim-group-${id}`}
            path={rxPath.map((p) => p.edgePath).join(" ")}
            stroke={rxStroke}
            larguraLinha={larguraLinha}
            isRx={true}
            mode={mode}
          />
        )}
      {txPath.map(({ edgePath, insertIndex }, index) => (
        <ClickableBaseEdge
          onDoubleClick={(event: any) =>
            handleCreateEdge(event, id, insertIndex)
          }
          key={`edge${id}_tx_${index}`}
          path={edgePath as string}
          markerEnd={"false"}
          className={isDown && !editMode ? "edge-pulse" : ""}
          style={{
            ...styles.clickableBaseEdge(
              selected,
              editMode,
              colors,
              edgeOpacity,
              larguraLinha,
              animation,
              isDown,
              easyMapsConfig,
            ),
            stroke: txStroke,
          }}
        />
      ))}

      {lineAnimation &&
        animation !== "desativada" &&
        !isDown &&
        !editMode &&
        ((variant === "trafego" &&
          !isNaN(data?.tx_traffic) &&
          data?.tx_traffic !== 0) ||
          (variant === "potencia" &&
            !isNaN(data?.tx_power) &&
            data?.tx_power !== 0)) &&
        txPath.length > 0 && (
          <EdgeAnimation
            key={`tx-anim-group-${id}`}
            path={txPath.map((p) => p.edgePath).join(" ")}
            stroke={txStroke}
            larguraLinha={larguraLinha}
            isRx={false}
            mode={mode}
          />
        )}

      {/* No modo de edição a caixa vira um contorno vazio e sem clique, só
          para mostrar onde ela fica. O conteúdo continua renderizado mas
          invisível (visibility), para a caixa manter a largura/altura reais.
          data-edge-label é lido pelo auto layout para medir a caixa. */}
      {showLabelEnlace && (
        <EdgeLabelRenderer key={`edge${id}_label`}>
          <div
            data-edge-label={id}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${positionLabel?.x}px,${positionLabel?.y}px)`,
              pointerEvents: editMode ? "none" : "all",
              zIndex: editMode ? 0 : 1000,
              ...(editMode && {
                borderRadius: 6,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                // outline (não border) para não somar no tamanho medido
                outline: `1px dashed ${alpha(theme.palette.text.primary, 0.35)}`,
                outlineOffset: -1,
              }),
            }}
          >
            <div style={editMode ? { visibility: "hidden" } : undefined}>
              {renderView()}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}

      {editMode &&
        selected &&
        positionHandlers.map(({ x, y, active }, handlerIndex) => (
          <EdgeLabelRenderer key={`edge${id}_handler${handlerIndex}`}>
            <div
              data-active={active ?? -1}
              style={styles.divEdge(active as number, x, y)}
              className="nodrag nopan"
              onMouseMove={(event) => handleMouseMove(event, id, handlerIndex)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDoubleClick={(event: MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
              }
            >
              <button
                data-active={active ?? -1}
                style={styles.buttonDraggable(active as number)}
                className="edgebutton"
                onMouseDown={(event) =>
                  handleMouseDown(event, id, handlerIndex, active as number)
                }
                onContextMenu={(event) =>
                  handleDeleteEdge(event, id, handlerIndex)
                }
              ></button>
            </div>
          </EdgeLabelRenderer>
        ))}
    </>
  );
};

export default memo(PositionableEdge);
